import { getAuthHeaders } from '../../../../utils/utils';
import {
  greApiUrl,
  shouldUsePrepTestsGreApi,
} from './greApiPaths';
import { normalizeGreTestSummaryPayload } from './greResultCache';

const PENDING_RECORD_PREFIX = 'pending:';

const isPendingRecordId = (recordId) =>
  String(recordId || '').startsWith(PENDING_RECORD_PREFIX);

const getPendingMockTestId = (recordId) =>
  String(recordId).slice(PENDING_RECORD_PREFIX.length);

const mapEssayTypeToTaskType = (essayType = '') => {
  const normalized = String(essayType).toLowerCase();

  if (normalized.includes('argument')) {
    return 'argument';
  }

  return 'issue';
};

const pickFirstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const asLegacyTest = (rawTest = {}, requestedTestId = null) => {
  const mockTestId =
    rawTest.mock_test_id ||
    rawTest.test_id ||
    rawTest.external_test_id ||
    requestedTestId;

  return {
    id: mockTestId || rawTest.id,
    db_id: rawTest.db_id ?? rawTest.id,
    test_name: rawTest.test_name,
    test_type: rawTest.test_type,
    status: rawTest.status,
    start_time: rawTest.start_time || rawTest.started_at,
    end_time: rawTest.end_time || rawTest.completed_at,
    created_at:
      rawTest.end_time ||
      rawTest.completed_at ||
      rawTest.created_at,
    time_spent_seconds: rawTest.time_spent_seconds,
    raw_score: rawTest.raw_score,
    scaled_score: pickFirstDefined(
      rawTest.scaled_score,
      rawTest.total_score
    ),
    total_score: pickFirstDefined(
      rawTest.total_score,
      rawTest.scaled_score
    ),
    percentage_score: pickFirstDefined(
      rawTest.percentage_score,
      rawTest.accuracy
    ),
    verbal_score: rawTest.verbal_score,
    quantitative_score: pickFirstDefined(
      rawTest.quantitative_score,
      rawTest.quant_score
    ),
    awa_score: rawTest.awa_score,
    total_questions: rawTest.total_questions,
    correct_answers: rawTest.correct_answers,
    user_answers: rawTest.user_answers || {},
    detailed_results: rawTest.detailed_results || {},
  };
};

export const normalizeGreTestResultsPayload = (data, requestedTestId = null) => {
  if (!data) {
    return null;
  }

  if (data.success && data.test) {
    return {
      success: true,
      test: asLegacyTest(data.test, requestedTestId),
      previous: data.previous || [],
      total_attempts: data.total_attempts || 1,
    };
  }

  if (data.test) {
    return {
      success: true,
      test: asLegacyTest(data.test, requestedTestId),
      previous: data.previous || [],
      total_attempts: data.total_attempts || 1,
    };
  }

  if (Array.isArray(data)) {
    const match = data.find(
      (entry) =>
        entry?.mock_test_id === requestedTestId ||
        entry?.test_id === requestedTestId ||
        String(entry?.id) === String(requestedTestId)
    );

    if (match) {
      return {
        success: true,
        test: asLegacyTest(match, requestedTestId),
        previous: [],
        total_attempts: 1,
      };
    }

    return {
      success: true,
      tests: data.map((entry) => asLegacyTest(entry, requestedTestId)),
    };
  }

  if (data.tests && Array.isArray(data.tests)) {
    if (requestedTestId) {
      const match = data.tests.find(
        (entry) =>
          entry?.mock_test_id === requestedTestId ||
          entry?.test_id === requestedTestId ||
          String(entry?.id) === String(requestedTestId)
      );

      if (match) {
        return {
          success: true,
          test: asLegacyTest(match, requestedTestId),
          previous: data.previous || [],
          total_attempts: data.total_attempts || 1,
        };
      }
    }

    return {
      success: data.success ?? true,
      tests: data.tests.map((entry) => asLegacyTest(entry, requestedTestId)),
      previous: data.previous || [],
      total_attempts: data.total_attempts || data.tests.length,
    };
  }

  if (data.id && (data.test_type || data.status || data.total_questions !== undefined)) {
    return {
      success: true,
      test: asLegacyTest(data, requestedTestId),
      previous: [],
      total_attempts: 1,
    };
  }

  return data;
};

const buildPrepTestsAttemptBody = (metadata = {}, results = {}) => {
  const mockTestId =
    metadata.test_id ||
    (isPendingRecordId(metadata.recordId)
      ? getPendingMockTestId(metadata.recordId)
      : null);

  const sections =
    results.sections ||
    (results.detailed_results?.section_results
      ? Object.entries(results.detailed_results.section_results).map(
          ([sectionKey, sectionValue]) => ({
            section_key: sectionKey,
            ...sectionValue,
          })
        )
      : results.sectionData
        ? [results.sectionData]
        : []);

  return {
    test_type: metadata.test_type || results.test_type || 'full_test',
    sections,
    total_questions: pickFirstDefined(
      results.total_questions,
      metadata.total_questions,
      0
    ),
    total_time_seconds: pickFirstDefined(
      results.time_spent_seconds,
      results.total_time_seconds,
      metadata.total_time_seconds
    ),
    mock_test_id: mockTestId,
    test_id: mockTestId,
    test_name: metadata.test_name,
    time_limit_minutes: metadata.time_limit_minutes,
    is_mock_exam: metadata.is_mock_exam,
    difficulty_level: metadata.difficulty_level,
    topics_covered: metadata.topics_covered,
    time_spent_seconds: results.time_spent_seconds,
    raw_score: results.raw_score,
    scaled_score: results.scaled_score,
    percentage_score: results.percentage_score,
    verbal_score: results.verbal_score,
    quantitative_score: results.quantitative_score,
    quant_score: results.quantitative_score,
    awa_score: results.awa_score,
    correct_answers: results.correct_answers,
    user_answers: results.user_answers,
    detailed_results: results.detailed_results,
  };
};

const extractRecordId = (data) =>
  data?.test_id ??
  data?.id ??
  data?.test?.id ??
  data?.test?.test_id ??
  null;

export async function fetchGreTestResultsRaw(testId) {
  const response = await fetch(
    `${greApiUrl('/tests')}?test_id=${encodeURIComponent(testId)}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch GRE test results (${response.status})`);
  }

  const data = await response.json();
  const normalized = normalizeGreTestResultsPayload(data, testId);

  if (normalized?.test || normalized?.tests) {
    return normalized;
  }

  if (shouldUsePrepTestsGreApi()) {
    const listResponse = await fetch(greApiUrl('/tests'), {
      headers: getAuthHeaders(),
    });

    if (listResponse.ok) {
      const listData = await listResponse.json();
      return normalizeGreTestResultsPayload(listData, testId);
    }
  }

  return normalized;
}

export async function fetchUserCompletedTests() {
  try {
    const response = await fetch(greApiUrl('/tests'), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    const normalized = normalizeGreTestResultsPayload(data);

    if (Array.isArray(normalized?.tests)) {
      return normalized.tests;
    }

    if (data.success) {
      return data.tests || [];
    }

    if (Array.isArray(data)) {
      return data.map((entry) => asLegacyTest(entry));
    }

    return [];
  } catch (error) {
    console.error('Error fetching completed tests:', error);
    return [];
  }
}

export async function fetchGreTestSummary() {
  try {
    const response = await fetch(greApiUrl('/tests/summary'), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return normalizeGreTestSummaryPayload(data);
  } catch (error) {
    console.error('Error fetching GRE test summary:', error);
    return [];
  }
}

export async function createGreTestRecord(metadata = {}) {
  if (shouldUsePrepTestsGreApi()) {
    const mockTestId = metadata.test_id || 'unknown-test';
    return `${PENDING_RECORD_PREFIX}${mockTestId}`;
  }

  const response = await fetch(greApiUrl('/tests'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(metadata),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data) {
    return null;
  }

  return extractRecordId(data);
}

export async function startGreTestRecord(recordId) {
  if (!recordId || shouldUsePrepTestsGreApi() || isPendingRecordId(recordId)) {
    return true;
  }

  const response = await fetch(greApiUrl(`/tests/${recordId}/start`), {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  return response.ok;
}

export async function completeGreTestRecord(
  recordId,
  results = {},
  metadata = {}
) {
  if (!recordId) {
    return null;
  }

  if (shouldUsePrepTestsGreApi() || isPendingRecordId(recordId)) {
    const response = await fetch(greApiUrl('/tests/attempt'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(
        buildPrepTestsAttemptBody({ ...metadata, recordId }, results)
      ),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return null;
    }

    return {
      ...data,
      test_id: extractRecordId(data),
    };
  }

  const response = await fetch(greApiUrl(`/tests/${recordId}/complete`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(results),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return null;
  }

  return data;
}

export async function saveGreTestSection(recordId, sectionData = {}, metadata = {}) {
  if (!recordId) {
    return null;
  }

  if (shouldUsePrepTestsGreApi() || isPendingRecordId(recordId)) {
    const response = await fetch(greApiUrl('/tests/attempt'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(
        buildPrepTestsAttemptBody(
          { ...metadata, recordId },
          {
            sectionData,
            sections: [sectionData],
            total_questions: sectionData.total_questions || 0,
            time_spent_seconds: sectionData.time_spent_seconds,
            user_answers: sectionData.user_answers,
            detailed_results: sectionData.detailed_results,
          }
        )
      ),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return null;
    }

    return data?.section_id ?? data?.id ?? extractRecordId(data);
  }

  const response = await fetch(greApiUrl(`/tests/${recordId}/sections`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(sectionData),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return null;
  }

  return data?.section_id ?? data?.id ?? null;
}

export async function submitGreAwaFeedback({
  testId,
  essayType,
  essayText,
  wordCount,
  promptText,
}) {
  const parsedTestId = Number(testId);
  const payload = shouldUsePrepTestsGreApi()
    ? {
        essay_text: essayText,
        task_type: mapEssayTypeToTaskType(essayType),
        prompt_text: promptText || null,
        test_id: Number.isFinite(parsedTestId) ? parsedTestId : null,
      }
    : {
        test_id: testId || 'local-test',
        essay_type: essayType || 'issue_task',
        essay_text: essayText,
        word_count: wordCount,
      };

  const response = await fetch(greApiUrl('/awa/feedback'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data) {
    return null;
  }

  if (data.success === false) {
    return null;
  }

  return data.feedback ?? data;
}
